/**
 * New course and New scenario (plan docs/plans/lms-plan.md 8.1 "New course:
 * title, slug (derived, editable until first publish), category, kind,
 * validity, renewal lead days, description; the first draft is created empty
 * with the template for the kind"; 6.5 "Scenarios ... get a New scenario
 * button that pre-fills the template"; 18.11's scenario profile; WP-44; R8,
 * R10).
 *
 * POST /admin/courses creates the course and its first draft from the
 * server's template for the kind (for best_practices: one section with the
 * situation and its decision, the principle and the site rule). A scenario
 * is a best_practices quiz with no expiry (6.5); the dialog pre-fills that,
 * and once the draft exists it adds what the template leaves empty (one
 * objective, two takeaways, two closing lines and three questions drawn in
 * full, model.ts scenarioStarter) with one PUT. Then the editor opens.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import Modal from '../../../../ui/Modal';
import { useToast } from '../../../../ui/Toast';
import { primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { apiErrorMessage } from '../../../api/client';
import { createCourse, getVersion, saveDraft } from '../../../api/catalog';
import type { NewCourseBody } from '../../../api/catalog';
import type { CourseCategory, CourseKind } from '../../../content/types';
import { COURSE_CATEGORIES, COURSE_KINDS } from '../../../content/types';
import { noticeCls } from '../../../components/player/styles';
import { NumberInput, SelectField, TextArea, TextInput } from './fields';
import { CATEGORY_LABELS, catalogPaths, KIND_LABELS, profileHint, scenarioStarter } from './model';

/** The backend's slugify (courses/validate.js), so the slug shown is the slug the server derives. */
export function slugify(title: string): string {
  return String(title || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/g, '');
}

export interface NewCourseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** 'scenario' pre-fills a best-practices scenario (6.5). */
  variant: 'course' | 'scenario';
}

export function NewCourseDialog({ isOpen, onClose, variant }: NewCourseDialogProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const scenario = variant === 'scenario';
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [category, setCategory] = useState<CourseCategory>(scenario ? 'best_practices' : 'custom');
  const [kind, setKind] = useState<CourseKind>('quiz');
  const [validity, setValidity] = useState<number | null>(scenario ? null : 12);
  const [renewalLead, setRenewalLead] = useState<number | null>(null);
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Opening the dialog starts from its variant's pre-fill.
  useEffect(() => {
    if (!isOpen) return;
    setTitle('');
    setSlug('');
    setSlugEdited(false);
    setCategory(scenario ? 'best_practices' : 'custom');
    setKind('quiz');
    setValidity(scenario ? null : 12);
    setRenewalLead(null);
    setDescription('');
    setError(null);
  }, [isOpen, scenario]);

  const shownSlug = slugEdited ? slug : slugify(title);
  const canSubmit = title.trim() !== '' && shownSlug !== '' && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const body: NewCourseBody = {
        title: title.trim(),
        slug: shownSlug,
        category,
        kind,
        validity_months: validity,
        description: description.trim() ? description.trim() : null,
      };
      if (renewalLead !== null) body.renewal_lead_days = renewalLead;
      const created = await createCourse(body);
      const draftId = created.draft_version?.id ?? null;
      if (!draftId) {
        toast(`${created.title} created.`);
        navigate(catalogPaths.course(created.id));
        return;
      }
      if (category === 'best_practices' && kind === 'quiz') {
        try {
          const detail = await getVersion(draftId);
          await saveDraft(draftId, scenarioStarter(detail));
        } catch (err) {
          // The course and its draft exist; the starter is a convenience (G10: say so, do not hide it).
          toast(apiErrorMessage(err, 'The scenario was created, but its starter questions could not be added.'), { type: 'error' });
        }
      }
      toast(`${created.title} created as a draft.`);
      navigate(catalogPaths.version(draftId));
    } catch (err) {
      setError(apiErrorMessage(err, 'The course could not be created.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={busy ? () => {} : onClose} title={scenario ? 'New scenario' : 'New course'} maxWidth="max-w-xl">
      <form
        className="space-y-3"
        data-testid="lms-new-course-dialog"
        data-variant={variant}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {scenario ? (
          <p className="text-sm text-gray-600 dark:text-gray-300" data-testid="lms-scenario-template-note">
            {profileHint('best_practices')}
          </p>
        ) : null}
        <TextInput label="Title" value={title} onChange={setTitle} testId="lms-new-title" />
        <TextInput
          label="Slug (the course's short name in links; editable until the first publish)"
          value={shownSlug}
          onChange={(v) => {
            setSlugEdited(true);
            setSlug(v);
          }}
          testId="lms-new-slug"
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <SelectField
            label="Category"
            value={category}
            onChange={(v) => setCategory(v as CourseCategory)}
            options={COURSE_CATEGORIES.filter((c) => c !== 'sop').map((c) => ({ value: c, label: CATEGORY_LABELS[c] }))}
            testId="lms-new-category"
            disabled={scenario}
            hint={scenario ? undefined : 'SOP courses are made from the SOP library (11.2).'}
          />
          <SelectField
            label="Kind"
            value={kind}
            onChange={(v) => setKind(v as CourseKind)}
            options={COURSE_KINDS.map((k) => ({ value: k, label: KIND_LABELS[k] }))}
            testId="lms-new-kind"
            disabled={scenario}
          />
          <NumberInput
            label="Valid for (months)"
            value={validity}
            onChange={setValidity}
            min={1}
            max={120}
            allowEmpty
            placeholder="No expiry"
            testId="lms-new-validity"
            hint={scenario ? 'A scenario never expires (6.5).' : 'Empty means a completion never expires.'}
          />
          <NumberInput
            label="Renewal assigned (days before expiry)"
            value={renewalLead}
            onChange={setRenewalLead}
            min={0}
            max={365}
            allowEmpty
            placeholder="Network default"
            testId="lms-new-renewal"
          />
        </div>
        <TextArea label="Description" value={description} onChange={setDescription} rows={2} testId="lms-new-description" />
        {error ? (
          <div className={noticeCls('red')} role="alert" data-testid="lms-new-course-error">
            {error}
          </div>
        ) : null}
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className={primaryBtnCls} disabled={!canSubmit} data-testid="lms-new-course-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            {scenario ? 'Create scenario' : 'Create course'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default NewCourseDialog;
